import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDepartment } from '../context/DepartmentContext'
import { getSalesTransactions, getMorningLogs, getSurplus, getFlags, getAllDailySummaries } from '../lib/store'
import { formatR, formatPct, calcStats, formatDateShort } from '../lib/calculations'
import { getSeverityBadge } from '../lib/flagging'
import { getCategories } from '../data/categories'
import { BAKERY_CATEGORIES_SEED } from '../data/bakeryData'
import { YAWEHS_CATEGORIES_SEED } from '../data/yawehsData'

function getCategoriesForDept(dept) {
  if (dept === 'yawehs') return YAWEHS_CATEGORIES_SEED
  if (dept === 'bakery') return BAKERY_CATEGORIES_SEED
  return getCategories()
}
import {
  TrendingUp, TrendingDown, AlertTriangle, ClipboardList, Package, DollarSign,
  Calendar, Upload, Minus
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts'
import WeatherDashboard from '../components/weather/WeatherWidget'

function StatCard({ label, value, sub, icon: Icon, trend, color = 'text-white' }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-xs mb-1">{label}</p>
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
        </div>
        {Icon && <Icon size={20} className="text-gray-600" />}
      </div>
      {trend !== undefined && trend !== null && (
        <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {trend >= 0 ? '+' : ''}{trend.toFixed(1)}% vs last period
        </div>
      )}
    </div>
  )
}

export default function Dashboard() {
  const { currentDepartmentId } = useDepartment()
  const today = new Date().toISOString().split('T')[0]

  const data = useMemo(() => {
    const dept = currentDepartmentId
    const todaySales = getSalesTransactions(dept, today)
    const todayMorning = getMorningLogs(dept, today)
    const todayFlags = getFlags(dept, { date: today })
    const todaySurplus = getSurplus(dept, today)

    const totalToday = todaySales.reduce((s, t) => s + (t.salesRands || 0), 0)
    const papEntry = todayMorning.find(m => m.category === 'pap only')
    const highFlags = todayFlags.filter(f => f.severity === 'HIGH' && !f.resolved)

    // Category breakdown today
    const catSales = {}
    for (const tx of todaySales) {
      catSales[tx.category] = (catSales[tx.category] || 0) + (tx.salesRands || 0)
    }
    const topCats = Object.entries(catSales)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)

    // Waste alerts
    const wasteAlerts = []
    for (const cat of getCategoriesForDept(currentDepartmentId)) {
      const opening = todayMorning.find(m => m.category === cat.name)?.value || 0
      const surplus = todaySurplus.find(s => s.category === cat.name)?.value || 0
      if (opening > 0 && (surplus / opening) > 0.3) {
        wasteAlerts.push({ category: cat.label, pct: ((surplus / opening) * 100).toFixed(0) })
      }
    }

    // 14-day sparkline
    const summaries = getAllDailySummaries(dept)
    const last14 = summaries
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14)
      .map(s => ({ date: formatDateShort(s.date), sales: s.totalSales || 0 }))

    // Week-to-date
    const d = new Date(today)
    const dayOfWeek = d.getDay()
    const weekStart = new Date(d)
    weekStart.setDate(d.getDate() - dayOfWeek)
    const weekStartStr = weekStart.toISOString().split('T')[0]
    const wtdSales = summaries
      .filter(s => s.date >= weekStartStr && s.date <= today)
      .reduce((sum, s) => sum + (s.totalSales || 0), 0)

    // Last week same period
    const lastWeekStart = new Date(weekStart)
    lastWeekStart.setDate(lastWeekStart.getDate() - 7)
    const lastWeekEnd = new Date(d)
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 7)
    const lwStartStr = lastWeekStart.toISOString().split('T')[0]
    const lwEndStr = lastWeekEnd.toISOString().split('T')[0]
    const lwSales = summaries
      .filter(s => s.date >= lwStartStr && s.date <= lwEndStr)
      .reduce((sum, s) => sum + (s.totalSales || 0), 0)

    const weekTrend = lwSales > 0 ? ((wtdSales - lwSales) / lwSales) * 100 : null

    // Month-to-date
    const monthStart = today.slice(0, 8) + '01'
    const mtdSales = summaries
      .filter(s => s.date >= monthStart && s.date <= today)
      .reduce((sum, s) => sum + (s.totalSales || 0), 0)

    // Last month same period
    const thisMonth = d.getMonth()
    const thisYear = d.getFullYear()
    const lastMonthDate = new Date(thisYear, thisMonth - 1, 1)
    const lmStart = lastMonthDate.toISOString().split('T')[0]
    const lmEndDate = new Date(thisYear, thisMonth - 1, d.getDate())
    const lmEnd = lmEndDate.toISOString().split('T')[0]
    const lmSales = summaries
      .filter(s => s.date >= lmStart && s.date <= lmEnd)
      .reduce((sum, s) => sum + (s.totalSales || 0), 0)

    const monthTrend = lmSales > 0 ? ((mtdSales - lmSales) / lmSales) * 100 : null

    return {
      totalToday,
      papQty: papEntry?.value || 0,
      highFlags,
      topCats,
      wasteAlerts,
      sparkline: last14,
      wtdSales,
      weekTrend,
      mtdSales,
      monthTrend,
    }
  }, [currentDepartmentId, today])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Dashboard</h1>
        <span className="text-gray-500 text-sm">{new Date().toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 flex-wrap">
        {currentDepartmentId === 'kitchen' ? (
          <>
            <Link to="/ingestion?type=morning" className="flex items-center gap-2 bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded-lg text-sm transition-colors">
              <ClipboardList size={16} /> Log Morning Stock
            </Link>
            <Link to="/ingestion?type=sales" className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
              <DollarSign size={16} /> Log Sales
            </Link>
            <Link to="/ingestion?type=surplus" className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
              <Package size={16} /> Log Surplus
            </Link>
          </>
        ) : (
          <>
            <Link to="/ingestion?type=production" className="flex items-center gap-2 bg-brand-700 hover:bg-brand-600 text-white px-4 py-2 rounded-lg text-sm transition-colors">
              <ClipboardList size={16} /> Log Production
            </Link>
            <Link to="/ingestion?type=distribution" className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
              <Package size={16} /> Log Distribution
            </Link>
            <Link to="/ingestion?type=damages" className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
              <DollarSign size={16} /> Log Damages
            </Link>
            {currentDepartmentId === 'yawehs' && (
              <Link to="/ingestion?type=cash" className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">
                <DollarSign size={16} /> Cash on Hand
              </Link>
            )}
          </>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Today's Sales" value={formatR(data.totalToday)} icon={DollarSign} color="text-green-400" />
        {currentDepartmentId === 'kitchen' && (
          <StatCard label="Pap Qty (Footfall)" value={`${data.papQty} kg`} icon={Calendar} />
        )}
        <StatCard label="Week-to-Date" value={formatR(data.wtdSales)} trend={data.weekTrend} icon={TrendingUp} />
        <StatCard label="Month-to-Date" value={formatR(data.mtdSales)} trend={data.monthTrend} icon={TrendingUp} />
      </div>

      {/* Weather & Production Suggestions */}
      <WeatherDashboard />

      {/* Active HIGH Flags */}
      {data.highFlags.length > 0 && (
        <div className="bg-red-950/50 border border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-red-400" />
            <span className="text-red-400 font-medium text-sm">{data.highFlags.length} Active HIGH Flags</span>
          </div>
          <div className="space-y-2">
            {data.highFlags.slice(0, 5).map(f => (
              <div key={f.id} className="flex items-center gap-2 text-xs">
                <span className={`px-1.5 py-0.5 rounded text-xs ${getSeverityBadge(f.severity)}`}>{f.flagCode}</span>
                <span className="text-gray-300">{f.description}</span>
              </div>
            ))}
            {data.highFlags.length > 5 && (
              <Link to="/flags" className="text-red-400 text-xs hover:underline">View all {data.highFlags.length} flags</Link>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 14-Day Trend */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-4">14-Day Sales Trend</h3>
          {data.sparkline.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={data.sparkline}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
                <Tooltip
                  contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#9ca3af' }}
                  formatter={(v) => [`R${v}`, 'Sales']}
                />
                <Line type="monotone" dataKey="sales" stroke="#dd6456" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-gray-600 text-sm">No data yet</div>
          )}
        </div>

        {/* Top Categories */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-4">Top Categories Today</h3>
          {data.topCats.length > 0 ? (
            <div className="space-y-3">
              {data.topCats.map(([cat, sales], i) => (
                <div key={cat} className="flex items-center gap-3">
                  <span className="text-gray-500 text-xs w-4">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm capitalize">{cat}</span>
                      <span className="text-sm text-green-400">{formatR(sales)}</span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full">
                      <div
                        className="h-1.5 bg-brand-500 rounded-full"
                        style={{ width: `${(sales / data.topCats[0][1]) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-600 text-sm">No sales today</div>
          )}
        </div>
      </div>

      {/* Waste Alerts */}
      {data.wasteAlerts.length > 0 && (
        <div className="bg-amber-950/30 border border-amber-800 rounded-lg p-4">
          <h3 className="text-amber-400 text-sm font-medium mb-2">Waste Alerts Today</h3>
          <div className="flex flex-wrap gap-2">
            {data.wasteAlerts.map(w => (
              <span key={w.category} className="bg-amber-900/50 text-amber-300 text-xs px-2 py-1 rounded">
                {w.category}: {w.pct}% surplus
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
